import { apiGet, apiRequest } from "./client.js";
import { fetchEveryPage, fetchPage, type Page } from "./collection.js";
import {
  parsePlaylist,
  parsePlaylistDetail,
  parsePlaylistItems,
  parsePlaylists,
  parseSchedules,
  parseSlots,
} from "./parse.js";
import type {
  Playlist,
  PlaylistDetail,
  PlaylistItem,
  PlaylistMode,
  Schedule,
  ScheduleSlot,
} from "./types.js";

/**
 * Playlists and schedules: global objects, and every route here is written that way.
 *
 * Not one of these calls takes a feed. A playlist is a way of choosing from the one
 * library and a schedule says which playlist governs which hours; feeds name them,
 * any number of feeds may name the same one, and nothing here is scoped by, filtered
 * by or owned by a feed. Which feeds use a given playlist is worked out from the
 * feeds — see `usage.ts` — because that is the direction the reference actually runs.
 */

const PLAYLISTS_PATH = "/api/playlists";
const SCHEDULES_PATH = "/api/schedules";

const playlistPath = (playlistId: string, suffix = "") =>
  `${PLAYLISTS_PATH}/${encodeURIComponent(playlistId)}${suffix}`;

const schedulePath = (scheduleId: string, suffix = "") =>
  `${SCHEDULES_PATH}/${encodeURIComponent(scheduleId)}${suffix}`;

/** One page of the playlist collection, for the screen that shows it. */
export async function fetchPlaylistPage(
  cursor: string | null,
  limit: number,
  signal?: AbortSignal,
): Promise<Page<Playlist>> {
  return fetchPage(PLAYLISTS_PATH, parsePlaylists, { cursor, limit, signal });
}

/**
 * Every playlist. Read by the controls that must offer all of them — a feed's
 * programming references, a slot's playlist — where showing the first page as if it
 * were the set would quietly hide the rest.
 */
export async function fetchPlaylists(
  signal?: AbortSignal,
): Promise<Playlist[]> {
  return fetchEveryPage(PLAYLISTS_PATH, parsePlaylists, { signal });
}

export async function fetchPlaylist(
  playlistId: string,
  signal?: AbortSignal,
): Promise<PlaylistDetail | null> {
  return parsePlaylistDetail(
    await apiGet<unknown>(playlistPath(playlistId), { signal }),
  );
}

export interface PlaylistCreateInput {
  name: string;
  mode: PlaylistMode;
  min_track_separation_minutes?: number;
  min_artist_separation_minutes?: number;
  enabled?: boolean;
}

export type PlaylistPatchInput = Partial<PlaylistCreateInput>;

export async function createPlaylist(
  input: PlaylistCreateInput,
): Promise<Playlist | null> {
  return parsePlaylist(
    await apiRequest<unknown>(PLAYLISTS_PATH, { method: "POST", body: input }),
  );
}

export async function patchPlaylist(
  playlistId: string,
  input: PlaylistPatchInput,
): Promise<Playlist | null> {
  return parsePlaylist(
    await apiRequest<unknown>(playlistPath(playlistId), {
      method: "PATCH",
      body: input,
    }),
  );
}

export async function deletePlaylist(playlistId: string): Promise<void> {
  await apiRequest<void>(playlistPath(playlistId), { method: "DELETE" });
}

/**
 * The order, written whole.
 *
 * `PUT /api/playlists/{id}/items` replaces the entire ordered list and assigns the
 * positions itself, one per thousand. That is what makes reordering a single request
 * that either takes or does not: there is no sequence of moves to land half of, and
 * no position arithmetic on this side that could disagree with the API's.
 */
export async function replacePlaylistItems(
  playlistId: string,
  items: readonly { trackId: string; weight: number }[],
): Promise<PlaylistItem[]> {
  const body = await apiRequest<unknown>(playlistPath(playlistId, "/items"), {
    method: "PUT",
    body: {
      items: items.map((item) => ({
        track_id: item.trackId,
        weight: item.weight,
      })),
    },
  });
  const raw: unknown =
    typeof body === "object" && body !== null
      ? (body as { items?: unknown }).items
      : null;
  return parsePlaylistItems(raw);
}

export async function fetchSchedules(
  signal?: AbortSignal,
): Promise<Schedule[]> {
  return parseSchedules(await apiGet<unknown>(SCHEDULES_PATH, { signal }));
}

export interface ScheduleCreateInput {
  name: string;
  timezone: string;
  enabled?: boolean;
}

export async function createSchedule(
  input: ScheduleCreateInput,
): Promise<Schedule | null> {
  const created = parseSchedules({
    schedules: [
      await apiRequest<unknown>(SCHEDULES_PATH, {
        method: "POST",
        body: input,
      }),
    ],
  });
  return created[0] ?? null;
}

export async function fetchSlots(
  scheduleId: string,
  signal?: AbortSignal,
): Promise<ScheduleSlot[]> {
  return parseSlots(
    await apiGet<unknown>(schedulePath(scheduleId, "/slots"), { signal }),
  );
}

export interface SlotInput {
  /** Carried so a replace keeps a slot's identity rather than minting a new one. */
  id?: string;
  playlist_id: string;
  days_of_week: number[];
  starts_at: string;
  ends_at: string;
  priority: number;
  enabled: boolean;
}

/** Slots are replaced whole, for the same reason playlist items are. */
export async function replaceSlots(
  scheduleId: string,
  slots: readonly SlotInput[],
): Promise<ScheduleSlot[]> {
  return parseSlots(
    await apiRequest<unknown>(schedulePath(scheduleId, "/slots"), {
      method: "PUT",
      body: { slots },
    }),
  );
}
